/**
 * Edit an existing fixture.
 *
 * The same generated {@link FixtureForm} the create screen uses, opened in edit
 * mode: it loads the fixture's own kind and config so the shape matches what it
 * was entered as, prefills from the current state, and submits a PATCH. A
 * SCHEDULED fixture is amended; a LIVE or COMPLETED one is corrected on the
 * append-only amendment log.
 */

import { useParams } from "react-router-dom";

import { FixtureForm, type EntryInitial, type FixtureInitial } from "@/components/fixtures/FixtureForm";
import { Crumbs, PageHead, Problem } from "@/components/kit";
import { getFixture, getFixtureForm } from "@/lib/api/client";
import type { EnvelopeView, ParticipantRef } from "@/lib/api/types";
import type { FormValue } from "@/lib/form";
import { displayName } from "@/lib/format";
import { useApi } from "@/spa/useApi";

/** An entrant as it comes back on the envelope → what the form prefills from. */
function toEntry(p: ParticipantRef, rank?: number | null): EntryInitial {
  if (p.placeholder) return { placeholderLabel: p.name };
  return { participant: p.code, rank: rank != null ? String(rank) : undefined };
}

/** The current entrants, whichever shape the fixture reads back as. */
function entriesOf(env: EnvelopeView): EntryInitial[] {
  if (env.leaderboard && env.leaderboard.length > 0) {
    return env.leaderboard.map((row) => toEntry(row.participant, row.rank));
  }
  return (env.participants ?? []).map((p) => toEntry(p));
}

/** The fixture's own facts as the form's initial values. */
function initialOf(env: EnvelopeView): FixtureInitial {
  const format: Record<string, FormValue> = {};
  for (const [key, value] of Object.entries(env.fixture.format ?? {})) {
    format[key] = typeof value === "boolean" ? value : String(value);
  }
  return {
    name: env.fixture.name ?? undefined,
    venue: env.fixture.venue?.code ?? undefined,
    scheduledStart: env.fixture.scheduledStart ?? undefined,
    status: env.status,
    competition: env.competitionCode ?? undefined,
    format,
    entries: entriesOf(env),
  };
}

export function EditFixturePage() {
  const { sport = "", id = "" } = useParams<{ sport: string; id: string }>();

  const { data, loading, error } = useApi(
    async () => {
      const env = await getFixture(id);
      const context = await getFixtureForm(
        sport,
        env.fixtureType ?? undefined,
        env.formatCode ?? undefined,
      );
      return { env, context };
    },
    [sport, id],
    "failed to load fixture",
  );

  if (error) return <Problem>Could not load the fixture to edit: {error}</Problem>;
  if (!data) return <PageHead title="Edit fixture" note={loading ? "Loading…" : ""} />;

  const { env, context } = data;
  const code = env.fixture.code ?? id;
  const scheduled = env.status === "SCHEDULED" || env.status === "POSTPONED";

  return (
    <>
      <Crumbs
        trail={[
          { label: "Sports", href: "/sports" },
          { label: displayName(sport), href: `/${sport}` },
          { label: "Fixtures", href: `/${sport}/fixtures` },
          { label: env.fixture.name ?? code, href: `/fixtures/${id}` },
          { label: scheduled ? "Edit" : "Correct" },
        ]}
      />
      <PageHead
        title={scheduled ? `Edit ${env.fixture.name ?? code}` : `Correct ${env.fixture.name ?? code}`}
        note={
          scheduled
            ? "Change the entrants, schedule or details. Recorded on the fixture's amendment history."
            : "This fixture is live or finished, so an edit is an append-only correction — the record is never rewritten."
        }
      />
      <FixtureForm context={context} editing={{ id, code, initial: initialOf(env) }} />
    </>
  );
}
